import { gql, GraphQLClient } from "graphql-request";
import { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { APIResponse } from "types/APIResponse";
import { AuthUser, User } from "types/Users";
import {
  AUTH_EVENT,
  STORAGE_KEY,
  clearAuthPayload,
  enterGuestMode,
  exitGuestMode,
  getAuthScheme,
  isGuestMode,
  persistAuthPayload,
} from "./useAuthUtils";
import { AUTH_EXPIRED_EVENT, logoutServer } from "../utils/apiClient";

export {
  AUTH_EVENT,
  GUEST_KEY,
  STORAGE_KEY,
  clearAuthPayload,
  enterGuestMode,
  exitGuestMode,
  isGuestMode,
  persistAuthPayload,
} from "./useAuthUtils";

// Minimal process.env declaration (kept after imports to satisfy import/first)
declare const process: { env: Record<string, string | undefined> };

export function useAuth() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [loginError, setLoginError] = useState("");
  const [purchases, setPurchases] = useState<any[]>([]);
  // Effective supporter perks, resolved server-side. See constants/supporterProducts.
  const [perks, setPerks] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const profileSyncInFlight = useRef(false);

  const graphQLClientRef = useRef<GraphQLClient | null>(null);

  // Initialize GraphQL client
  if (!graphQLClientRef.current) {
    graphQLClientRef.current = new GraphQLClient(
      process.env.REACT_APP_API_URL + "/graphql",
      {
        headers: {
          authorization: "",
        },
        credentials: "include",
        responseMiddleware: (res) => {
          // Handle 401 Unauthorized - token expired or invalid
          if (res instanceof Response && res.status === 401) {
            console.warn("Session expired (401), logging out");
            clearAuthPayload();
            window.location.href = "/Login";
            return;
          }

          const newToken = (res as any)?.headers?.get?.("x-new-token");
          if (newToken && user) {
            const updatedUser = { ...user, token: newToken };
            setUser(updatedUser);
            persistAuthPayload({ user: updatedUser, purchases });
            graphQLClientRef.current?.setHeader(
              "authorization",
              `${getAuthScheme(newToken)} ${newToken}`
            );
          }
        },
      }
    );
  }

  const graphQLClient = graphQLClientRef.current;

  const loadUserFromStorage = () => {
    try {
      const jsonUser = secureLocalStorage.getItem(STORAGE_KEY) as string;
      if (jsonUser) {
        const userData = JSON.parse(jsonUser);
        setUser(userData.user);
        setIsLoggedIn(true);
        setIsGuest(false);
        setPurchases(userData.purchases || []);
        setPerks(userData.perks || []);
        if (userData.user.roles?.includes("administrator")) {
          setIsAdmin(true);
        }
        return;
      }
    } catch (err) {
      console.error("Failed to parse stored auth data", err);
    }
    setIsLoggedIn(false);
    setUser(undefined);
    setPurchases([]);
    setPerks([]);
    setIsAdmin(false);
    setIsGuest(isGuestMode());
  };

  useEffect(() => {
    loadUserFromStorage();
    const scope: any = typeof globalThis === "undefined" ? null : globalThis;
    if (scope?.addEventListener) {
      const handler = () => {
        // Defer reload so state updates happen outside render phase
        setTimeout(() => loadUserFromStorage(), 0);
      };
      scope.addEventListener(AUTH_EVENT, handler);
      return () => scope.removeEventListener(AUTH_EVENT, handler);
    }
    return undefined;
  }, []);

  // Keep GraphQL auth header in sync when token changes
  useEffect(() => {
    if (user?.token) {
      graphQLClient.setHeader(
        "authorization",
        `${getAuthScheme(user.token)} ${user.token}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  const refreshPurchases = async (_userId: number, token: string) => {
    try {
      const client = graphQLClientRef.current;
      if (!client) return purchases;
      client.setHeader("authorization", `${getAuthScheme(token)} ${token}`);
      const data = await client.request<{ refreshPurchases: any[] }>(gql`
        mutation RefreshPurchases {
          refreshPurchases
        }
      `);
      if (data?.refreshPurchases) {
        setPurchases(data.refreshPurchases);
        return data.refreshPurchases;
      }
      return purchases;
    } catch (error) {
      console.error("Failed to refresh purchases", error);
      return purchases;
    }
  };

  const refreshLocalData = async () => {
    if (!user?.ID || !user?.token) return;

    try {
      const [userData, latestPurchases] = await Promise.all([
        getAppUserData(user.ID),
        refreshPurchases(user.ID, user.token),
      ]);

      const newData = { ...user, ...userData };
      setUser(newData);

      persistAuthPayload({ user: newData, purchases: latestPurchases });
    } catch (error) {
      console.error("Failed to refresh local data", error);
    }
  };

  useEffect(() => {
    if (
      !user?.token ||
      profileSyncInFlight.current ||
      user.oursexplorationlist_current_session_id !== undefined
    ) {
      return;
    }
    profileSyncInFlight.current = true;
    refreshLocalData()
      .catch((err) => console.warn("Failed to hydrate invite profile", err))
      .finally(() => {
        profileSyncInFlight.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.ID, user?.token, user?.oursexplorationlist_current_session_id]);

  async function getAppUserData(user_id: number): Promise<User> {
    const query = gql`
      query getUser($user_id: Int!) {
        getUser(user_id: $user_id) {
          user_id
          created_at
          updated_at
          oursexplorationlist_current_session_id
          excluded_topics
          oursexplorationlist_simplified_version
          timezone
        }
      }
    `;
    const graphResponse: any = await graphQLClient.request(query, { user_id });
    const userData = graphResponse.getUser;
    if (userData.length === 0) {
      await createAppUser(user_id);
      return getAppUserData(user_id);
    } else {
      return userData[0];
    }
  }

  const createAppUser = async (user_id: number) => {
    const query = gql`
      mutation createUser($user_id: Int!) {
        createUser(user_id: $user_id) {
          success
          message
          affectedRows
          id
        }
      }
    `;
    const graphResponse: any = await graphQLClient.request(query, { user_id });
    const response: APIResponse = graphResponse;

    return response.id;
  };

  const login = async (
    username: string,
    password: string,
    rememberMe = false
  ) => {
    setLoginError("");
    setUser(undefined);
    setIsLoggedIn(false);

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          username: username,
          password: password,
          rememberMe: rememberMe,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const { success, data } = await response.json();
      if (success) {
        // A returning user is no longer a guest
        exitGuestMode();
        setIsGuest(false);
        // Set authorization header on the existing client
        graphQLClient.setHeader("authorization", `JWT ${data.user.token}`);

        const appUserData = await getAppUserData(Number.parseInt(data.user.ID));
        const totalUser = { ...data.user, ...appUserData };
        setIsLoggedIn(true);
        setUser(totalUser);
        persistAuthPayload({
          user: totalUser,
          purchases: data.purchases,
          perks: data.perks || [],
        });
        if (data.user.roles.includes("administrator")) {
          setIsAdmin(true);
        }
        return true;
      } else {
        setIsLoggedIn(false);
        setUser(undefined);
        setLoginError("Username or password is incorrect.");
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      setIsLoggedIn(false);
      setUser(undefined);
      setLoginError(
        "Unable to connect to the server. Please check your connection and try again."
      );
      return false;
    }
  };

  const register = async (email: string, password: string) => {
    setLoginError("");
    setUser(undefined);
    setIsLoggedIn(false);

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        }
      );

      const { success, data, message } = await response.json();
      if (response.ok && success) {
        // New account holder is no longer a guest
        exitGuestMode();
        setIsGuest(false);
        // Set authorization header on the existing client
        graphQLClient.setHeader("authorization", `JWT ${data.user.token}`);

        const appUserData = await getAppUserData(Number.parseInt(data.user.ID));
        const totalUser = { ...data.user, ...appUserData };
        setIsLoggedIn(true);
        setUser(totalUser);
        persistAuthPayload({
          user: totalUser,
          purchases: data.purchases || [],
          perks: data.perks || [],
        });
        return true;
      } else {
        setLoginError(
          message || "We couldn't create your account. Please try again."
        );
        return false;
      }
    } catch (error) {
      console.error("Register error:", error);
      setLoginError(
        "Unable to connect to the server. Please check your connection and try again."
      );
      return false;
    }
  };

  const enterGuest = () => {
    enterGuestMode();
    setIsGuest(true);
  };

  const logout = () => {
    logoutServer().catch(() => {});
    clearAuthPayload();
    exitGuestMode();
    setIsLoggedIn(false);
    setUser(undefined);
    setPurchases([]);
    setPerks([]);
    setIsAdmin(false);
    setIsGuest(false);
  };

  useEffect(() => {
    const onExpired = () => {
      console.warn("Session expired - logging out");
      logout();
    };
    globalThis.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => {
      globalThis.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
    };
  }, []);

  const updateOurSexplorationListCurrentSessionID = async (
    oursexplorationlist_current_session_id: number
  ) => {
    const query = gql`
      mutation updateUserSexplorationListCurrentSession(
        $oursexplorationlist_current_session_id: ID!
      ) {
        updateUserSexplorationListCurrentSession(
          oursexplorationlist_current_session_id: $oursexplorationlist_current_session_id
        ) {
          success
          message
          affectedRows
          id
        }
      }
    `;

    const graphResponse: any = await graphQLClient.request(query, {
      oursexplorationlist_current_session_id,
    });
    const response: APIResponse = graphResponse;

    await refreshLocalData();

    return response;
  };

  const updateUserSettings = async (
    excluded_topics: string,
    oursexplorationlist_simplified_version: number
  ) => {
    const query = gql`
      mutation updateUserSettings(
        $excluded_topics: String!
        $oursexplorationlist_simplified_version: Int!
      ) {
        updateUserSettings(
          excluded_topics: $excluded_topics
          oursexplorationlist_simplified_version: $oursexplorationlist_simplified_version
        ) {
          success
          message
          affectedRows
          id
        }
      }
    `;

    const graphResponse: any = await graphQLClient.request(query, {
      excluded_topics,
      oursexplorationlist_simplified_version,
    });
    const response: APIResponse = graphResponse.updateUserSettings;

    await refreshLocalData();

    return response;
  };

  const impersonate = async (targetUserId: number) => {
    // isAdmin is derived from the signed-in user's roles, so it already implies
    // a user exists. Checked explicitly so the token read below is provably safe.
    if (!isAdmin || !user) {
      console.error("Only admins can impersonate users");
      return false;
    }

    try {
      // Store the real admin user before impersonating
      const realAdmin = {
        user: { ...user },
        purchases: [...purchases],
      };
      secureLocalStorage.setItem("real-admin", JSON.stringify(realAdmin));

      const response = await fetch(
        process.env.REACT_APP_API_URL + "/impersonate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${user.token}`,
          },
          body: JSON.stringify({
            targetUserId,
          }),
        }
      );

      const { success, data } = await response.json();
      if (success) {
        // Set authorization header on the existing client
        graphQLClient.setHeader("authorization", `JWT ${data.user.token}`);

        const appUserData = await getAppUserData(Number.parseInt(data.user.ID));
        const totalUser = { ...data.user, ...appUserData };
        setIsLoggedIn(true);
        setUser(totalUser);
        persistAuthPayload({
          user: totalUser,
          purchases: data.purchases,
          perks: data.perks || [],
        });
        setPurchases(data.purchases);
        setPerks(data.perks || []);
        // Keep admin status so they can switch back
        setIsAdmin(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Impersonation failed:", error);
      return false;
    }
  };

  const stopImpersonating = () => {
    const realAdminData = secureLocalStorage.getItem("real-admin");
    if (realAdminData) {
      const {
        user: adminUser,
        purchases: adminPurchases,
        perks: adminPerks,
      } = JSON.parse(realAdminData as string);

      // Set authorization header on the existing client
      graphQLClient.setHeader("authorization", `JWT ${adminUser.token}`);

      setUser(adminUser);
      setPurchases(adminPurchases);
      setPerks(adminPerks || []);
      persistAuthPayload({
        user: adminUser,
        purchases: adminPurchases,
        perks: adminPerks || [],
      });
      setIsAdmin(true);
      secureLocalStorage.removeItem("real-admin");
    }
  };

  const isImpersonating = () => {
    return !!secureLocalStorage.getItem("real-admin");
  };

  // Note: the access token's JWT expiry is intentionally NOT used to drive
  // logout. The refresh-token flow in apiClient transparently mints new
  // access tokens on 401. Logout only fires when refresh itself fails
  // (handled via AUTH_EXPIRED_EVENT above) or the user explicitly logs out.

  return {
    login,
    logout,
    refreshLocalData,
    isLoggedIn,
    user,
    purchases,
    perks,
    loginError,
    updateOurSexplorationListCurrentSessionID,
    updateUserSettings,
    isAdmin,
    impersonate,
    stopImpersonating,
    isImpersonating,
    register,
    enterGuest,
    isGuest,
  };
}
